import React, { useState, useEffect } from 'react';
import './Common.css';
import { apiService } from '../api/api';

// ─────────────────────────────────────────────────────────────────────────────
// ForgotPasswordPage handles 3 entry points:
//
// A) "Forgot Password?" button on Login page
//    → onNavigate('forgotPassword', { email, skipToStep: 'sent' })
//    → Shows "Check Your Email" screen (📧)
//    → User clicks link in email → browser opens with token in URL → goes to B
//
// B) User clicks reset link in email
//    → URL: /reset-password?email=foo@bar.com&token=CfDJ8...
//    → Layout.js reads token+email from URL, saves to sessionStorage (synchronously)
//    → Renders this page, which reads sessionStorage → shows Set Password form
//
// C) Direct navigation to /forgot-password (no token)
//    → Shows "Enter your email" form (step 1)
// ─────────────────────────────────────────────────────────────────────────────

const ForgotPasswordPage = ({ onNavigate, initialEmail = '', skipToStep = 1 }) => {

  // Read token + email from sessionStorage (written by Layout.js when reset URL detected)
  const sessionToken = sessionStorage.getItem('resetToken') || '';
  const sessionEmail = sessionStorage.getItem('resetEmail') || '';
  const isFromEmailLink = Boolean(sessionToken);

  // Resolved values: sessionStorage takes priority over props
  const resolvedToken = sessionToken;
  const resolvedEmail = sessionEmail || initialEmail;

  // ── Determine starting step ──────────────────────────────────────────────
  // Priority: email link token > skipToStep prop > default (1)
  const [step, setStep] = useState(() => {
    if (isFromEmailLink)        return 2;        // came from reset link → set password
    if (skipToStep === 'sent')  return 'sent';   // came from "Forgot Password?" button
    if (skipToStep === 2)       return 2;
    return 1;
  });

  const [token]      = useState(resolvedToken);
  const [emailInput, setEmailInput] = useState(resolvedEmail);

  // The email shown on the "sent" screen (from props when coming from Login page)
  const [sentEmail]  = useState(initialEmail || resolvedEmail);

  const [passwordData, setPasswordData]               = useState({ newPassword: '', confirmPassword: '' });
  const [errors, setErrors]                           = useState({});
  const [loading, setLoading]                         = useState(false);
  const [showNewPassword, setShowNewPassword]         = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Clear sessionStorage after reading — token should not survive a page refresh
  useEffect(() => {
    sessionStorage.removeItem('resetToken');
    sessionStorage.removeItem('resetEmail');
    sessionStorage.removeItem('isResetLink');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 1 email input (for the manual "enter your email" flow)
  const [sendEmailInput, setSendEmailInput] = useState(initialEmail);

  // ─── Step 1 handler: send reset email ────────────────────────────────────
  const handleSendResetEmail = async (e) => {
    e.preventDefault();
    setErrors({});
    const trimmed = sendEmailInput.trim();
    if (!trimmed)                       { setErrors({ email: 'Email is required' }); return; }
    if (!/\S+@\S+\.\S+/.test(trimmed)) { setErrors({ email: 'Please enter a valid email address' }); return; }

    setLoading(true);
    try {
      await apiService.forgotPassword(trimmed);
      setStep('sent');
    } catch (error) {
      setErrors({
        email: error.response?.data?.message || error.response?.data?.error || 'Failed to send reset email. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  // ─── Step 2 handler: submit new password ─────────────────────────────────
  const handleResetPassword = async (e) => {
    e.preventDefault();
    const newErrors = {};

    // If no token from email link, user must type their email manually
    if (!isFromEmailLink && !token) {
      if (!emailInput.trim()) {
        newErrors.email = 'Please enter your email address';
      } else if (!/\S+@\S+\.\S+/.test(emailInput.trim())) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    if (!passwordData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }
    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    setLoading(true);
    setErrors({});
    // console.log('📤 resetPassword →', { email: emailInput.trim(), tokenPreview: token ? token.slice(0, 30) + '...' : '(none)' });

    try {
      await apiService.resetPassword({
        email:       emailInput.trim(),
        token:       token.trim(),
        newPassword: passwordData.newPassword,
      });
      // console.log('✅ Password reset successful');
      setStep(3);
    } catch (error) {
      console.error('❌ resetPassword failed:', error);
      setErrors({
        submit:
          error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to reset password. The link may have expired — please request a new one.',
      });
    } finally {
      setLoading(false);
    }
  };

  // ─── STEP 1: Enter email ──────────────────────────────────────────────────
  // if (step === 1) {
  //   return (
  //     <div className="login-page">
  //       <div className="login-container">
  //         <div className="login-card">
  //           <div className="login-header">
  //             <h1>Forgot Password?</h1>
  //             <p>Enter your email and we'll send you a password reset link.</p>
  //           </div>
  //           <form onSubmit={handleSendResetEmail} className="login-form">
  //             <div className="form-group">
  //               <label htmlFor="sendEmail">Email Address</label>
  //               <div className="form-group-content">
  //                 <input
  //                   type="email" id="sendEmail" name="sendEmail"
  //                   value={sendEmailInput}
  //                   onChange={(e) => { setSendEmailInput(e.target.value); if (errors.email) setErrors({}); }}
  //                   className={`form-control ${errors.email ? 'error' : ''}`}
  //                   placeholder="Enter your registered email"
  //                   disabled={loading} autoFocus
  //                 />
  //                 {errors.email && <span className="error-message">{errors.email}</span>}
  //               </div>
  //             </div>
  //             <button type="submit" className="login-button" disabled={loading}>
  //               {loading ? 'Sending...' : 'Send Reset Link'}
  //             </button>
  //             <button type="button" onClick={() => onNavigate('login')} style={backBtnStyle}>
  //               ← Back to Login
  //             </button>
  //           </form>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  // ─── SENT: Check your email ───────────────────────────────────────────────
  // Shown after "Forgot Password?" button is clicked on Login page
  if (step === 'sent') {
    const displayEmail = sendEmailInput || sentEmail;
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <h1>Check Your Email</h1>
            </div>
            <div style={{ textAlign: 'center', padding: '10px 0 24px' }}>
              <div style={{ fontSize: '56px', marginBottom: '16px' }}>📧</div>
              <p style={{ color: '#333', fontSize: '15px', marginBottom: '8px' }}>
                A password reset link has been sent to{' '}
                {displayEmail
                  ? <><br /><strong>{displayEmail}</strong></>
                  : 'your email address'
                }.
              </p>
              <p style={{ color: '#666', fontSize: '14px', marginTop: '12px' }}>
                Click the link in the email to set your new password.
                The link will open this page automatically with your identity verified.
              </p>
            </div>
            <button type="button" onClick={() => onNavigate('login')} style={backBtnStyle}>
              ← Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── STEP 2: Set new password ─────────────────────────────────────────────
  if (step === 2) {
    const hasToken      = Boolean(token);
    const hasEmailKnown = Boolean(resolvedEmail || emailInput);

    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <h1>Set New Password</h1>
              <p>
                {hasToken && resolvedEmail
                  ? `Reset link verified for ${resolvedEmail}. Enter your new password below.`
                  : 'Enter your email and new password below.'
                }
              </p>
            </div>

            {/* ✅ Green banner — token + email both confirmed */}
            {hasToken && resolvedEmail && (
              <div style={successBannerStyle}>
                ✅ Identity verified for <strong>{resolvedEmail}</strong>
              </div>
            )}

            {/* ℹ️ Info banner — token found but no email (shouldn't happen with current C# setup) */}
            {hasToken && !resolvedEmail && (
              <div style={infoBannerStyle}>
                🔐 Reset token verified. Please enter your email and new password.
              </div>
            )}

            <form onSubmit={handleResetPassword} className="login-form">
              {errors.submit && (
                <div style={errorBannerStyle}>
                  ⚠️ {errors.submit}
                  {(errors.submit.includes('expired') || errors.submit.includes('invalid')) && (
                    <div style={{ marginTop: '8px' }}>
                      <button
                        type="button"
                        onClick={() => { setStep(1); setErrors({}); }}
                        style={{ background: 'none', border: 'none', color: '#3498db', cursor: 'pointer', fontSize: '13px', padding: 0 }}
                      >
                        Request a new reset link →
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Email field — only shown when no token (manual flow) */}
              {!hasToken && (
                <div className="form-group">
                  <label htmlFor="emailInput">Your Email Address</label>
                  <div className="form-group-content">
                    <input
                      type="email" id="emailInput" name="emailInput"
                      value={emailInput}
                      onChange={(e) => {
                        setEmailInput(e.target.value);
                        if (errors.email) setErrors(prev => ({ ...prev, email: '' }));
                      }}
                      className={`form-control ${errors.email ? 'error' : ''}`}
                      placeholder="Enter your registered email"
                      disabled={loading} autoFocus
                    />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                  </div>
                </div>
              )}

              {/* New Password */}
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <div className="form-group-content">
                  <div className="password-input-wrapper">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      id="newPassword" name="newPassword"
                      value={passwordData.newPassword}
                      onChange={(e) => {
                        setPasswordData(prev => ({ ...prev, newPassword: e.target.value }));
                        if (errors.newPassword) setErrors(prev => ({ ...prev, newPassword: '' }));
                      }}
                      className={`form-control ${errors.newPassword ? 'error' : ''}`}
                      placeholder="Enter new password (min 6 characters)"
                      disabled={loading}
                      autoFocus={hasToken}
                    />
                    <button type="button" className="toggle-password" onClick={() => setShowNewPassword(v => !v)}>
                      {showNewPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  {errors.newPassword && <span className="error-message">{errors.newPassword}</span>}
                </div>
              </div>

              {/* Confirm Password */}
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <div className="form-group-content">
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword" name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={(e) => {
                        setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }));
                        if (errors.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: '' }));
                      }}
                      className={`form-control ${errors.confirmPassword ? 'error' : ''}`}
                      placeholder="Confirm your new password"
                      disabled={loading}
                    />
                    <button type="button" className="toggle-password" onClick={() => setShowConfirmPassword(v => !v)}>
                      {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
                </div>
              </div>

              <button type="submit" className="login-button" disabled={loading}>
                {loading ? 'Resetting Password...' : 'Set New Password'}
              </button>

              <button type="button" onClick={() => onNavigate('login')} style={backBtnStyle}>
                ← Back to Login
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ─── STEP 3: Success ──────────────────────────────────────────────────────
  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header"><h1>Password Reset!</h1></div>
          <div style={{ textAlign: 'center', padding: '10px 0 24px' }}>
            <div style={{ fontSize: '56px', marginBottom: '16px' }}>✅</div>
            <p style={{ color: '#155724', fontSize: '15px', marginBottom: '8px' }}>
              Your password has been successfully reset.
            </p>
            <p style={{ color: '#555', fontSize: '14px' }}>
              You can now log in with your new password.
            </p>
          </div>
          <button type="button" className="login-button" onClick={() => onNavigate('login')}>
            Go to Login
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const backBtnStyle = {
  width: '100%', marginTop: '10px', padding: '12px',
  border: 'none', background: 'transparent', color: '#3498db',
  cursor: 'pointer', fontSize: '14px', fontWeight: '500',
};

const successBannerStyle = {
  margin: '0 0 18px', padding: '12px 14px',
  backgroundColor: '#d4edda', border: '1px solid #c3e6cb',
  borderRadius: '6px', color: '#155724', fontSize: '13px', lineHeight: '1.5',
};

const infoBannerStyle = {
  margin: '0 0 18px', padding: '12px 14px',
  backgroundColor: '#e8f4fd', border: '1px solid #bee5eb',
  borderRadius: '6px', color: '#0c5460', fontSize: '13px', lineHeight: '1.5',
};

const errorBannerStyle = {
  marginBottom: '15px', padding: '10px 12px',
  backgroundColor: '#fff3f3', border: '1px solid #f5c6cb',
  borderRadius: '6px', color: '#e74c3c', fontSize: '13px', textAlign: 'center',
};

export default ForgotPasswordPage;